import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { DeductionINvoiceSection } from '../../components/deduction-invoice-section.component';
import DeductionFilters from '../../components/deduction-filters.component';
import { PillTabs } from '../../components/dashboard/pill-tabs.component';
import DeductionTabs from '../../components/deduction-tabs.component';
import AddDeductionButton from '../../components/add-deduction-button.component';
import NotInvoicedSection from '../../components/not-invoiced-section.component';
import AddDeductionModal from '../../components/add-deduction-modal.component';
import { Trans, useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useRedux';
import { fetchInvoiceEditData } from '../../features/customer/customer.slice';
import { fetchTimesheetDeductions } from '../../features/timesheet/timesheet.slice';
import { useForm } from 'react-hook-form';
import { useDeductionPageSubmit } from '../../hooks/useDeductionPageSubmit.hook';
import './deduction.page.scss';
// Imported only for the .mi-hero class used by the new heading; all other
// rules in this stylesheet are scoped under .manage-invoice-v2 and won't
// affect this page's DOM.
import './../../scss/components/manage-invoice.style.scss';

/* Tiny sparkles icon for the hero badge. Inline so the page has no
   new icon-library dependency. */
const HeroSparkles = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
        <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
    </svg>
);

/* Render the title with the LAST word styled as a violet gradient brand
   accent — works for any locale. Single-word titles are fully styled. */
const renderHeroTitle = (title: string) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) {
        return <span className="mi-hero__brand">{trimmed}</span>;
    }
    const prefix = trimmed.slice(0, idx);
    const last = trimmed.slice(idx + 1);
    return (
        <>
            {prefix}{' '}
            <span className="mi-hero__brand">{last}</span>
        </>
    );
};

const DeductionSkeleton = () => (
    <div className="deduction-skeleton" aria-hidden="true">
        <div className="ded-skel ded-skel--header" />
        <div className="ded-skel ded-skel--filters" />
        <div className="ded-skel-table">
            <div className="ded-skel ded-skel--thead" />
            {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="ded-skel ded-skel--row" />
            ))}
        </div>
    </div>
);

function DeductionPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('not-invoiced');
    const [pageLoading, setPageLoading] = useState(true);

    useEffect(() => {
        const id = setTimeout(() => setPageLoading(false), 1000);
        return () => clearTimeout(id);
    }, []);

    // Get userId and customers from Redux
    const userId = useSelector((state: any) => state.auth?.user?.userId);

    // Clear filters when switching tabs
    useEffect(() => {
        // Set invoicing status based on active tab
        if (activeTab === 'invoiced') {
            setSelectedYear('invoiced');
        } else if (activeTab === 'not-invoiced') {
            setSelectedYear('non-invoiced');
        }
        setSelectedMonth('all');
        setSelectedClient('all');
        setSearchInput('');
        setStartDate('');
        setEndDate('');
    }, [activeTab]);
    const { customers, loading: customersLoading } = useSelector((state: any) => state.customer || { customers: [], loading: false });
    const globalData = useSelector((state: any) => state?.globalData?.data);

    // Filter states
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedClient, setSelectedClient] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isLoadingDeductions, setIsLoadingDeductions] = useState(false);
    const [invoicedLoadTrigger, setInvoicedLoadTrigger] = useState(0);

    // Search should only be considered when input length is more than 3
    const effectiveSearchQuery = useMemo(() => {
        const q = searchInput.trim();
        return q.length > 3 ? q : '';
    }, [searchInput]);

    // Modal state management
    const [showDeductionModal, setShowDeductionModal] = useState(false);
    const [editingDeductionData, setEditingDeductionData] = useState<any | null>(null);
    const [selectedCustomerForDeduction, setSelectedCustomerForDeduction] = useState<number | null>(null);

    // React Hook Form setup
    const {
        register,
        setValue,
        watch,
        formState: { errors },
        trigger,
        reset
    } = useForm();

    // Default preferences for deduction modal
    const preferences = useMemo(() => ({
        vat: 25,
        currency: 'SEK'
    }), []);

    // Use deduction submit hook
    const { handleDeductionSubmit } = useDeductionPageSubmit({
        editingDeductionData,
        selectedCustomerId: selectedCustomerForDeduction,
        preferences,
        globalData,
        userId: userId || 0
    });

    // Fetch customers on component mount
    useEffect(() => {
        if (userId) {
            dispatch(fetchInvoiceEditData(userId));
        }
    }, [userId, dispatch]);

    // Convert customers to dropdown options
    const customerOptions = useMemo(() => {
        return customers.map((customer: any) => ({
            value: String(customer.customerId || customer.id),
            label: customer.name || customer.customerName
        }));
    }, [customers]);

    // Tabs configuration
    const tabs = useMemo(() => [
        { key: 'invoiced', label: t('INVOICED') || 'Invoiced' },
        { key: 'not-invoiced', label: t('NOT_INVOICED') || 'Not Invoiced' }
    ], [t]);

    // Modal handlers
    const handleOpenModal = () => {
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
        setShowDeductionModal(true);
    };

    const handleOpenEditModal = (deductionData: any) => {
        setEditingDeductionData(deductionData);
        setSelectedCustomerForDeduction(deductionData.customerId || null);
        setShowDeductionModal(true);
    };

    const handleCloseModal = () => {
        setShowDeductionModal(false);
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
    };

    // Wrapper: on success → close modal + re-fetch the not-invoiced list
    const handleModalSubmit = async (deductionData: any) => {
        await handleDeductionSubmit(deductionData);
        // Success — close modal then refresh the list
        handleCloseModal();

        // Build params object - only include parameters that are actually selected
        const params: any = {
            pageNumber: 1,
            tab: 2 // Not invoiced tab
        };

        // Only add fromDate and toDate if both dates are selected
        if (startDate && startDate.trim() !== '' && endDate && endDate.trim() !== '') {
            params.fromDate = startDate;
            params.toDate = endDate;
        } else {
            if (selectedYear && selectedYear !== 'all' && selectedYear !== 'invoiced' && selectedYear !== 'non-invoiced') {
                const yearNum = parseInt(selectedYear);
                const monthNum = selectedMonth && selectedMonth !== 'all' ? parseInt(selectedMonth) : 1;
                params.year = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
            }

            if (selectedMonth && selectedMonth !== 'all' && selectedYear && selectedYear !== 'all' && selectedYear !== 'invoiced' && selectedYear !== 'non-invoiced') {
                const yearNum = parseInt(selectedYear);
                const monthNum = parseInt(selectedMonth);
                params.month = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
            }
        }

        // Only add customerId if a specific customer is selected
        if (selectedClient && selectedClient !== 'all' && selectedClient.trim() !== '') {
            params.customerId = selectedClient;
        }

        // Only add searchQuery if it has a value
        if (effectiveSearchQuery) {
            params.searchQuery = effectiveSearchQuery;
        }

        dispatch(fetchTimesheetDeductions(params));
    };

    const handleCustomerSelect = (customerId: number) => {
        setSelectedCustomerForDeduction(customerId);
    };

    // Handle invoicing status change (this controls the tab switching)
    const handleInvoicingStatusChange = (value: string) => {
        setSelectedYear(value);
        // Switch tabs based on invoicing status
        if (value === 'invoiced') {
            setActiveTab('invoiced');
        } else if (value === 'non-invoiced') {
            setActiveTab('not-invoiced');
        }
    };

    // Load button is enabled only when both dates are selected
    const isDateRangeSelected = Boolean(startDate?.trim() && endDate?.trim());

    // Load deductions based on current filters - only called on button click
    const handleLoadDeductions = async () => {
        if (!isDateRangeSelected) {
            return;
        }

        setIsLoadingDeductions(true);
        try {
            // Load deductions using date range filter only
            const params: any = {
                pageNumber: 1,
                tab:2,
                fromDate: startDate,
                toDate: endDate
            };
            if (activeTab === 'invoiced') {
                setInvoicedLoadTrigger(prev => prev + 1);
            } else {
                await dispatch(fetchTimesheetDeductions(params)).unwrap();
            }

        } catch (error) {
            console.error('Error loading deductions:', error);
        } finally {
            setIsLoadingDeductions(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'invoiced':
                return <DeductionINvoiceSection
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={effectiveSearchQuery}
                    loadTrigger={invoicedLoadTrigger}
                    fromDate={startDate}
                    toDate={endDate}
                />;
            case 'not-invoiced':
                return <NotInvoicedSection
                    onEditDeduction={handleOpenEditModal}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    customerId={selectedClient}
                    searchQuery={effectiveSearchQuery}
                />;
            default:
                return <DeductionINvoiceSection
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={effectiveSearchQuery}
                    loadTrigger={invoicedLoadTrigger}
                    fromDate={startDate}
                    toDate={endDate}
                />;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='deduction-invoice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    <div className='px-3 deduction-overview'>
                        {pageLoading ? (
                        <DeductionSkeleton />
                        ) : (
                        <>
                        {/* Hero header — same soft violet card used by My Customers,
                            Drafts and My Freelancers. The old deduction-overview-header
                            markup is replaced wholesale; .mi-hero handles its own
                            entrance animation. */}
                        <div className="mi-hero">
                            <span className="mi-hero__badge">
                                <HeroSparkles />
                                {t('DEDUCTION_CENTER', { defaultValue: 'Deduction Center' })}
                            </span>
                            <h1 className="mi-hero__title">
                                {renderHeroTitle(t('DEDUCTIONS_LABEL'))}
                            </h1>
                            {/* Two-paragraph subtitle. The translation
                                strings live in
                                translation/lang/subcomponents/newTimesheet*.js
                                and contain `<strong>…</strong>` markers
                                around UI element names (button / tab
                                names). `<Trans>` swaps those markers for
                                <strong className="mi-hero__subtitle-emph">
                                so the emphasised words render in violet
                                without us hand-splitting the sentences.
                                The `--wide` modifier removes the 620 px
                                cap so the long copy spans the full hero. */}
                            <p className="mi-hero__subtitle mi-hero__subtitle--wide">
                                <Trans
                                    i18nKey="DEDUCTION_DESCRIPTION"
                                    components={{ strong: <strong className="mi-hero__subtitle-emph" /> }}
                                />
                            </p>
                            <p className="mi-hero__subtitle mi-hero__subtitle--wide">
                                <Trans
                                    i18nKey="DEDUCTION_DESCRIPTION_FLOW"
                                    components={{ strong: <strong className="mi-hero__subtitle-emph" /> }}
                                />
                            </p>
                        </div>

                        {/* Invoiced / Not Invoiced switcher — replaces the
                            old dropdown that lived inside DeductionFilters.
                            Bound to the same `selectedYear` state and
                            `handleInvoicingStatusChange` handler the dropdown
                            used, so every downstream effect / API dispatch /
                            tab-sync keeps working exactly as before. */}
                        <div className="deduction-page__type-tabs-row ded-reveal" style={{ animationDelay: '110ms' }}>
                            <PillTabs
                                tabs={[
                                    { key: 'non-invoiced', label: t('NON_INVOICED_LABEL') || 'Not Invoiced' },
                                    { key: 'invoiced',     label: t('INVOICED_LABEL')     || 'Invoiced' },
                                ]}
                                activeTab={selectedYear === 'non-invoiced' ? 'non-invoiced' : 'invoiced'}
                                onTabChange={handleInvoicingStatusChange}
                                ariaLabel={t('INVOCING_STATUS') || 'Invoicing status'}
                            />
                        </div>

                        <div className="deduction-page__filters-row ded-reveal" style={{ animationDelay: '140ms' }}>
                            <DeductionFilters
                                selectedYear={selectedYear}
                                selectedMonth={selectedMonth}
                                selectedClient={selectedClient} 
                                searchQuery={searchInput}
                                startDate={startDate}
                                endDate={endDate}
                                onYearChange={handleInvoicingStatusChange}
                                onMonthChange={setSelectedMonth}
                                onClientChange={setSelectedClient}
                                onSearchChange={(e) => setSearchInput(e.target.value)}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                                clients={customerOptions}
                                loading={customersLoading}
                            />
                            <div className="deduction-page__actions">
                                <button 
                                    className="load-deduction-btn"
                                    onClick={handleLoadDeductions}
                                    disabled={isLoadingDeductions || !isDateRangeSelected}
                                >
                                    {isLoadingDeductions ? (
                                        <span className="load-deduction-btn__text">{t('LOADING') || 'Loading...'}</span>
                                    ) : (
                                        <>
                                            <span className="load-deduction-btn__text">{t('LOAD_DEDUCTIONS') || 'Load Deductions'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="divider-deductions"></div>
                            <div className="deduction-page__actions">
                                <AddDeductionButton onClick={handleOpenModal} />
                            </div>
                        </div>



                        <div className="deduction-page__content ded-reveal" style={{ animationDelay: '220ms' }}>
                            {renderTabContent()}
                        </div>
                        </>
                        )}
                    </div>

                </div>
            </div>

            {/* Add Deduction Modal */}
            <AddDeductionModal
                isOpen={showDeductionModal}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                trigger={trigger}
                t={t}
                editingDeductionData={editingDeductionData}
                preferences={preferences}
                showClientSelector={true}
                customers={customerOptions}
                selectedCustomerId={selectedCustomerForDeduction}
                onCustomerChange={handleCustomerSelect}
            />
        </div>
    );
}

export default DeductionPage;
